import { useState } from "react";
import { useGetCompanyProfiles, useCreateCompanyProfile, useUpdateCompanyProfile, useDeleteCompanyProfile, usePolishCompanyProfile, CompanyProfile, ProfilePolishResult } from "../useAiWorkbench";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Building2, Save, Trash2, Wand2, Sparkles, Check } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { useToast } from "@/hooks/use-toast";

export function CompanyProfilesTab() {
  const { data, isLoading } = useGetCompanyProfiles();
  const createProfile = useCreateCompanyProfile();
  const updateProfile = useUpdateCompanyProfile();
  const deleteProfile = useDeleteCompanyProfile();
  const polishProfile = usePolishCompanyProfile();
  const { toast } = useToast();

  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<CompanyProfile>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  
  const [polishPreview, setPolishPreview] = useState<ProfilePolishResult | null>(null);
  const [showPolishDialog, setShowPolishDialog] = useState(false);

  const profiles = data?.profiles ?? [];

  function handleSelect(p: CompanyProfile) {
    setSelectedProfileId(p.id);
    setFormData({ ...p, capabilities: p.capabilities, certifications: p.certifications });
  }

  function handleCreateNew() {
    setSelectedProfileId(null);
    setFormData({
      name: "新增公司資料",
      overview: "",
      relevantExperience: "",
      industryReputation: "",
      awards: "",
      capabilities: [],
      certifications: [],
      isDefault: false
    });
  }

  async function handleSave() {
    if (!formData.name) return;
    try {
      if (selectedProfileId) {
        await updateProfile.mutateAsync({ id: selectedProfileId, data: formData });
        toast({ title: "已更新公司資料" });
      } else {
        const res = await createProfile.mutateAsync(formData);
        setSelectedProfileId(res.profile.id);
        toast({ title: "已新增公司資料" });
      }
    } catch (e: any) {
      toast({ title: "錯誤", description: e.message, variant: "destructive" });
    }
  }

  async function handleDelete() {
    if (!deleteConfirmId) return;
    try {
      await deleteProfile.mutateAsync(deleteConfirmId);
      if (selectedProfileId === deleteConfirmId) {
        setSelectedProfileId(null);
        setFormData({});
      }
      toast({ title: "已刪除公司資料" });
    } catch (e: any) {
      toast({ title: "錯誤", description: e.message, variant: "destructive" });
    } finally {
      setDeleteConfirmId(null);
    }
  }

  async function handlePolish() {
    if (!selectedProfileId) return;
    try {
      const { polished } = await polishProfile.mutateAsync({ id: selectedProfileId, data: formData });
      setPolishPreview(polished);
      setShowPolishDialog(true);
    } catch (e: any) {
      toast({ title: "AI 潤飾錯誤", description: e.message, variant: "destructive" });
    }
  }

  function applyPolish() {
    if (!polishPreview) return;
    setFormData(prev => ({
      ...prev,
      overview: polishPreview.overview,
      relevantExperience: polishPreview.relevantExperience,
      industryReputation: polishPreview.industryReputation,
      awards: polishPreview.awards
    }));
    setShowPolishDialog(false);
    toast({ title: "已套用潤飾結果", description: "請記得點擊「儲存」以保留變更。" });
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[calc(100vh-12rem)] md:min-h-[600px]">
      {/* Left Pane - List */}
      <div className="md:col-span-1 border rounded-lg bg-card flex flex-col overflow-hidden max-h-[40vh] md:max-h-none">
        <div className="p-4 border-b flex items-center justify-between bg-muted/40">
          <h2 className="font-semibold flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            公司資料
          </h2>
          <Button size="sm" variant="outline" onClick={handleCreateNew} className="h-8 gap-1.5">
            <Plus className="w-3.5 h-3.5" />
            新增
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-md" />)
          ) : profiles.length === 0 ? (
            <div className="text-center p-6 text-muted-foreground text-sm">
              尚無公司資料。
            </div>
          ) : (
            profiles.map(p => (
              <button
                key={p.id}
                onClick={() => handleSelect(p)}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors flex justify-between items-center ${
                  selectedProfileId === p.id ? "bg-primary text-primary-foreground font-medium" : "hover:bg-muted"
                }`}
              >
                <span className="truncate">{p.name}</span>
                {p.isDefault && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-background/20 ml-2 shrink-0">預設</span>}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right Pane - Form */}
      <div className="md:col-span-2 border rounded-lg bg-card flex flex-col overflow-hidden">
        {(!selectedProfileId && !formData.name) ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-8 text-center">
            <Building2 className="w-12 h-12 mb-4 opacity-20" />
            <p className="font-medium">請選擇公司資料</p>
            <p className="text-sm mt-1">或新增一份以管理公司背景資訊。</p>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between bg-muted/20 shrink-0 gap-3">
              <h2 className="font-semibold">
                {selectedProfileId ? "編輯公司資料" : "新增公司資料"}
              </h2>
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                {selectedProfileId && (
                  <Button size="sm" variant="outline" className="h-8 gap-1.5 text-destructive hover:bg-destructive/10 border-destructive/20" onClick={() => setDeleteConfirmId(selectedProfileId)}>
                    <Trash2 className="w-3.5 h-3.5" />
                    刪除
                  </Button>
                )}
                {selectedProfileId && (
                  <Button size="sm" variant="outline" className="h-8 gap-1.5 border-primary/30 text-primary hover:bg-primary/10" onClick={handlePolish} disabled={polishProfile.isPending}>
                    {polishProfile.isPending ? <Sparkles className="w-3.5 h-3.5 animate-pulse" /> : <Wand2 className="w-3.5 h-3.5" />}
                    AI 潤飾
                  </Button>
                )}
                <Button size="sm" onClick={handleSave} disabled={createProfile.isPending || updateProfile.isPending} className="h-8 gap-1.5">
                  <Save className="w-3.5 h-3.5" />
                  儲存
                </Button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="space-y-2">
                <Label>資料名稱</Label>
                <Input value={formData.name || ""} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="例如：陳大文科技方案" />
              </div>

              <div className="space-y-2">
                <Label>公司簡介</Label>
                <Textarea rows={3} value={formData.overview || ""} onChange={e => setFormData({ ...formData, overview: e.target.value })} placeholder="公司的簡短描述..." />
              </div>

              <div className="space-y-2">
                <Label>相關經驗</Label>
                <Textarea rows={4} value={formData.relevantExperience || ""} onChange={e => setFormData({ ...formData, relevantExperience: e.target.value })} placeholder="過往項目、曾中標的項目或主要客戶..." />
              </div>

              <div className="space-y-2">
                <Label>行業聲譽</Label>
                <Textarea rows={2} value={formData.industryReputation || ""} onChange={e => setFormData({ ...formData, industryReputation: e.target.value })} placeholder="市場地位、服務質素..." />
              </div>

              <div className="space-y-2">
                <Label>獎項及殊榮</Label>
                <Textarea rows={2} value={formData.awards || ""} onChange={e => setFormData({ ...formData, awards: e.target.value })} placeholder="曾獲獎項、重要認可..." />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>服務範圍（以逗號分隔）</Label>
                  <Textarea rows={3} value={(formData.capabilities || []).join(", ")} onChange={e => setFormData({ ...formData, capabilities: e.target.value.split(",").map(s=>s.trim()).filter(Boolean) })} placeholder="軟件開發、網絡基建、顧問服務..." />
                </div>
                <div className="space-y-2">
                  <Label>專業認證（以逗號分隔）</Label>
                  <Textarea rows={3} value={(formData.certifications || []).join(", ")} onChange={e => setFormData({ ...formData, certifications: e.target.value.split(",").map(s=>s.trim()).filter(Boolean) })} placeholder="ISO 9001、AWS 認證..." />
                </div>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="isDefault" 
                  checked={!!formData.isDefault}
                  onCheckedChange={(c) => setFormData({ ...formData, isDefault: c === true })} 
                />
                <label htmlFor="isDefault" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  設為預設資料
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteConfirmId !== null}
        title="刪除公司資料"
        description="確定要刪除這份公司資料嗎？此操作無法還原。"
        confirmLabel="刪除"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmId(null)}
      />

      <Dialog open={showPolishDialog} onOpenChange={setShowPolishDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              AI 潤飾預覽
            </DialogTitle>
            <DialogDescription>
              檢閱 AI 潤飾的內容。套用後將覆蓋目前的草稿，請記得在套用後點擊「儲存」。
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-6 py-4 px-1">
            {[
              { label: "公司簡介", old: formData.overview, new: polishPreview?.overview },
              { label: "相關經驗", old: formData.relevantExperience, new: polishPreview?.relevantExperience },
              { label: "行業聲譽", old: formData.industryReputation, new: polishPreview?.industryReputation },
              { label: "獎項及殊榮", old: formData.awards, new: polishPreview?.awards }
            ].map(f => (
              <div key={f.label} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">目前：{f.label}</span>
                  <div className="p-3 bg-muted/30 rounded-md text-sm whitespace-pre-wrap text-muted-foreground">
                    {f.old || "無內容"}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">潤飾後：{f.label}</span>
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-md text-sm whitespace-pre-wrap">
                    {f.new || "無內容"}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPolishDialog(false)}>
              取消
            </Button>
            <Button onClick={applyPolish} className="gap-1.5">
              <Check className="w-4 h-4" />
              套用潤飾
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
